import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { Chat, ChatMapping, ChatRooms } from "../../api/chat/chat-interface";

const chats: ChatRooms = {
  Students: [],
  Parents: [],
  Teachers: [],
};

type UserType = "Students" | "Teachers" | "Parents";

const currentChat: Chat | null = null;

interface initialStateInterface {
  chats: ChatRooms;
  currentChat: Chat | null;
  currentChatType: UserType;
}

const initialState: initialStateInterface = {
  chats,
  currentChat,
  currentChatType: "Students",
};

const chatSlice = createSlice({
  name: "chatSlice",
  initialState,
  reducers: {
    setChats: (state, action: PayloadAction<ChatRooms>) => {
      state.chats = action.payload;
    },
    setCurrentChat: (state, action: PayloadAction<Chat>) => {
      state.currentChat = action.payload;
    },
    addMessage: (
      state,
      action: PayloadAction<{
        chatId: string;
        body: string;
        userType: UserType;
        creatorId: string;
        identifier: string;
      }>
    ) => {
      const { chatId, body, userType, creatorId, identifier } = action.payload;

      const chatIndex = state.chats[userType].findIndex(
        (chat) => chat._id === chatId
      );

      if (chatIndex !== -1) {
        // Extract the chat from its current position
        const [chat] = state.chats[userType].splice(chatIndex, 1);

        if (!chat.conversations) {
          chat.conversations = [];
        }

        // Add the new message to its conversations
        chat?.conversations.push({
          body,
          creatorId,
          dateSent: new Date(),
          identifier,
          readBy: [{ userId: creatorId }],
        });

        // Place the chat back to the top of the chats list
        state.chats[userType].unshift(chat);

        // Update the current chat
        state.currentChat = chat;
      }
    },
    setNewChat: (state, action: PayloadAction<ChatMapping>) => {
      if (!action.payload) return;
      // Extract the chat from the payload
      Object.entries(action.payload).forEach(([key, chat]) => {
        // check if chat already exists
        const chatIndex = state.chats[key as UserType].findIndex(
          (chatItem) => chatItem._id === chat._id
        );

        if (chatIndex === -1) {
          // Add the new chat to the top of the chats list
          state.chats[key as UserType].unshift(chat);
        }

        // else add the new message to the existing chat and place it at the top of the chats list
        else {
          // Extract the chat from its current position
          const [chatItem] = state.chats[key as UserType].splice(chatIndex, 1);

          // check the chat identifier
          if (
            chatItem.conversations[chatItem.conversations.length - 1]
              .identifier !== chat.identifier?.toString()
          ) {
            // push the new message to the conversations
            chatItem.conversations.push(chat.conversations[0]);
          }

          // Place the chat back to the top of the chats list
          state.chats[key as UserType].unshift(chatItem);

          // Update the current chat
          if (state.currentChat?._id === chatItem._id) {
            state.currentChat = chatItem;
          }
        }
      });
    },
    markAsRead: (
      state,
      action: PayloadAction<{
        chatId: string;
        userType: UserType;
        userId: string;
      }>
    ) => {
      const { chatId, userType, userId } = action.payload;

      const chatIndex = state.chats[userType].findIndex(
        (chat) => chat._id === chatId
      );

      if (chatIndex !== -1) {
        // Extract the chat from its current position
        const [chat] = state.chats[userType].splice(chatIndex, 1);

        if (!chat.conversations) {
          chat.conversations = [];
        }

        // update the last message read by the user if the user is not the creator
        if (
          chat.conversations.length &&
          chat.conversations[chat.conversations.length - 1]?.creatorId !==
            userId
        ) {
          chat.conversations.splice(-1, 1, {
            ...chat.conversations[chat.conversations.length - 1],
            readBy: [
              ...chat.conversations[chat.conversations.length - 1]?.readBy,
              { userId },
            ],
          });
        }
        // return the chat to its position
        state.chats[userType].splice(chatIndex, 0, chat);
      }
    },
    setCurrentChatType: (state, action: PayloadAction<UserType>) => {
      state.currentChatType = action.payload;
    },
  },
});

export const {
  setChats,
  setCurrentChat,
  addMessage,
  setNewChat,
  markAsRead,
  setCurrentChatType,
} = chatSlice.actions;

export const selectChats = (state: { chat: initialStateInterface }) =>
  state.chat;

export default chatSlice.reducer;
